import { useCallback, useRef, useState } from "react";

/**
 * Runs the commands a paired phone sends, against this page's own player.
 *
 * THE PLAYHEAD NEVER TRAVELS. The phone sends an intent and this runs it here,
 * which is the whole design. A relayed position is a sample with an age, and a
 * clip is anchored five seconds behind the press, so even a small staleness
 * moves the clip off the play the fan reacted to. Running it here reads the
 * position in the same instant a press of the buttons under the video would,
 * and it is also the only way a clip gets a thumbnail at all: that comes from
 * captureFrame reading the decoded video frame, which cannot be sent to a
 * phone and back.
 *
 * CLAIM BEFORE ACTING, ALWAYS. Every desktop tab on this game receives the
 * same channel event (see useSecondScreenSession's mount effect: "this
 * desktop, or a second tab of it, blocks too, and starts watching the same
 * stream"). Acting on the event directly would write one Moment per open tab
 * for one tap of GOAL. The claim is a conditional UPDATE server-side, so
 * exactly one tab gets a 200 and every other stops here, silently: the tab
 * that won owns the result too, and a loser reporting one would race it.
 */

export interface CommandPlayer {
  playReel(url: string, offsetMs: number): Promise<boolean>;
  backToLive(): Promise<{ ok: boolean; code: string; isLive: boolean }>;
  /**
   * Back to the game, then seek into it. One call rather than a restore, a
   * seek and a play from here, because the order matters and the player is
   * the only place that can see the reel state deciding it.
   */
  playClipAt(startMs: number, feed?: "home" | "away"): Promise<boolean>;
  /**
   * Whether the game is live, the player's own signal ORed with the page's.
   * Stamped onto every playback result so the phone, which cannot see the
   * video, can label its own back-to-live control.
   */
  isLive(): boolean;
}

interface Params {
  sessionId: string | null;
  jwt: string | null;
  apiBaseUrl?: string;
  /** The game this page is actually playing, for the wrong_game guard. */
  gameId: string;
  player?: CommandPlayer;
  /** The same emitter a press of the buttons under the video uses. */
  emitClipRequest: (type: string) => void;
  /**
   * Renews this page's session and hands back the new token, or null.
   *
   * A paired session outlives the tenant access token: the pairing lasts as
   * long as both devices stay connected, which for a hockey game is hours,
   * while the token is minutes. Without this, the first command issued after
   * an expiry 401s on its claim and this tab silently does nothing, which is
   * indistinguishable from another tab having won the claim, so nobody
   * reports anything and the fan's tap disappears.
   */
  refreshAuth?: () => Promise<string | null>;
}

export interface LastCommand {
  type: "clip" | "play_reel" | "play_clip" | "back_to_live";
  /** Human readable, for the blocked panel's card. */
  label: string;
}

interface ClaimedCommand {
  id: string;
  type: LastCommand["type"];
  payload: Record<string, unknown> | null;
  gameId: string;
}

export function useSecondScreenCommands({
  sessionId, jwt, apiBaseUrl, gameId, player, emitClipRequest, refreshAuth,
}: Params): { execute: (commandId: string) => Promise<void>; lastCommand: LastCommand | null } {
  const [lastCommand, setLastCommand] = useState<LastCommand | null>(null);

  /*
   * Everything execute reads, through one ref kept current on every render.
   *
   * execute is handed to useSecondScreenSession, which holds it for the life
   * of a stream connection. Rebuilding it whenever the player or the token
   * changed would either reopen that stream or leave it calling a stale copy,
   * so it is built once and reads forward instead.
   */
  const latest = useRef({ sessionId, jwt, apiBaseUrl: apiBaseUrl ?? "", gameId, player, emitClipRequest, refreshAuth });
  latest.current = { sessionId, jwt, apiBaseUrl: apiBaseUrl ?? "", gameId, player, emitClipRequest, refreshAuth };

  const execute = useCallback(async (commandId: string): Promise<void> => {
    const {
      sessionId: id, jwt: token, apiBaseUrl: base, gameId: game,
      player: p, emitClipRequest: emit, refreshAuth,
    } = latest.current;
    if (!id || !token) return;

    /*
     * Held in a local rather than read from `latest` at each call, because a
     * 401 below replaces it and both the claim and the result must then go out
     * with the SAME new token. Reading through the ref would take whatever the
     * last render happened to have, which is the stale one until React
     * re-renders this page.
     */
    let bearer = token;
    const headersFor = (t: string) => ({
      Authorization: `Bearer ${t}`,
      "Content-Type": "application/json",
    });
    const root = `${base}/second-screen/${id}/commands/${commandId}`;

    let command: ClaimedCommand;
    try {
      let claim = await fetch(`${root}/claim`, { method: "POST", headers: headersFor(bearer) });

      /*
       * A 401 is the one failure worth a second attempt.
       *
       * A pairing lasts as long as both devices stay connected, which for a
       * game is hours; this page's access token is good for minutes. So the
       * first command after an expiry finds a dead token, and every status
       * below treats a failed claim as "another tab won", which means nobody
       * reports anything and the fan's tap vanishes. Renew once and try again.
       *
       * Only once, and only on 401: a second failure is a real one, and
       * retrying 409 or 410 would be retrying a command another tab is already
       * running, or one that has expired.
       */
      if (claim.status === 401 && refreshAuth) {
        const renewed = await refreshAuth();
        if (renewed) {
          bearer = renewed;
          claim = await fetch(`${root}/claim`, { method: "POST", headers: headersFor(bearer) });
        }
      }

      /*
       * 409 another tab won, 410 too late, anything else a real failure. All
       * of them mean this tab does nothing further, INCLUDING reporting a
       * result: only the tab holding the claim may speak for this command.
       */
      if (!claim.ok) return;
      command = (await claim.json()) as ClaimedCommand;
    } catch {
      // A claim that never answered is a claim this tab does not hold.
      return;
    }

    const report = async (ok: boolean, code: string, detail?: Record<string, unknown>) => {
      try {
        // headersFor(bearer), so a token renewed during the claim above is the
        // one this goes out with too.
        await fetch(`${root}/result`, {
          method: "POST", headers: headersFor(bearer), body: JSON.stringify({ ok, code, detail }),
        });
      } catch {
        // The phone falls back to its own timeout and one poll of the
        // command's row. A result that could not be delivered is not worth
        // retrying against a command that expires in seconds.
      }
    };

    /*
     * The game this page is playing, versus the game the session was paired
     * to.
     *
     * Belt to the server's desktop_offline braces. That guard already covers
     * the ordinary case of a desktop that navigated away, because leaving the
     * page closes its stream and stamps desktop_seen_at. This catches whatever
     * is left, and it fails safe: it reports rather than touching the player.
     */
    if (command.gameId !== game) {
      await report(false, "wrong_game");
      return;
    }

    if (command.type === "clip") {
      const clipType = String(command.payload?.clipType ?? "");
      /*
       * The same emitter the buttons under the video use, so a clip made from
       * the phone runs the one clip-writing path there has ever been: real
       * position, real captured frame, one writer. See handleClipPost on this
       * page for why exactly one writer matters.
       */
      emit(clipType);
      setLastCommand({ type: "clip", label: `Clipped ${clipType.toUpperCase()}` });
      await report(true, "clipped");
      return;
    }

    /*
     * A fan's own clip, which is an offset into the GAME's video rather than a
     * reel of its own, on ONE of the game's two broadcasts.
     *
     * playClipAt puts the game back and switches feed before seeking, neither
     * of which is optional. While a highlight reel is loaded the game is not
     * what is playing, so the offset would land inside the reel at a position
     * that means nothing; and on the wrong broadcast it lands at the right
     * second of the wrong game. The whole sequence lives on the player rather
     * than here, because only there are the reel state, the time-shift window
     * and the player's own lifecycle visible.
     *
     * The phone cannot see the video, so the feed travels with the command
     * rather than being read off this screen: the fan picked a clip, and the
     * clip knows which broadcast it belongs to.
     */
    if (command.type === "play_clip") {
      if (!p) { await report(false, "no_player"); return; }
      const startMs = Number(command.payload?.startMs ?? 0);
      // A command from a phone built before feeds were recorded carries none,
      // and everything made then was a home clip.
      const feed = command.payload?.feed === "away" ? "away" : "home";
      const ok = await p.playClipAt(startMs, feed);
      if (ok) setLastCommand({ type: "play_clip", label: "Playing a clip from your phone" });
      // isLive rides along on every playback result, so the phone can label
      // its own control without a status channel of its own.
      await report(ok, ok ? "playing" : "player_declined", { isLive: p.isLive() });
      return;
    }

    if (command.type === "play_reel") {
      if (!p) { await report(false, "no_player"); return; }
      const manifestPath = String(command.payload?.manifestPath ?? "");
      const offsetMs = Number(command.payload?.offsetMs ?? 0);

      // OUR base url, not one the phone sent. The phone only ever sends a
      // root-relative path, so the only origin this can reach is the API this
      // page was already configured with.
      const ok = await p.playReel(`${base}${manifestPath}`, offsetMs);
      if (ok) setLastCommand({ type: "play_reel", label: "Playing a moment from your phone" });
      await report(ok, ok ? "playing" : "player_declined", { isLive: p.isLive() });
      return;
    }

    if (!p) { await report(false, "no_player"); return; }
    const result = await p.backToLive();
    // Back to the game means there is no longer anything to narrate.
    if (result.ok) setLastCommand(null);
    await report(result.ok, result.code, { isLive: result.isLive });
  }, []);

  return { execute, lastCommand };
}
